import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { WORKER_URL, getAuthHeaders } from '../../utils/api';

export default function Billing({ onPaymentSuccess }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [paid, setPaid] = useState(false);
  const pollingRef = useRef(null);
  const navigate = useNavigate();

  const fetchSub = async () => {
    try {
      const res = await fetch(`${WORKER_URL}/api/user/subscription`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSub(data);
        return data;
      }
    } catch {}
    return null;
  };

  useEffect(() => {
    fetchSub().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!sub?.invoice) return;
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      const data = await fetchSub();
      if (!data) return;
      if (!data.invoice || data.invoice.status === 'paid') {
        clearInterval(pollingRef.current);
        setPaid(true);
        onPaymentSuccess?.();
        setTimeout(() => navigate('/'), 2000);
      }
    }, 5000);

    return () => clearInterval(pollingRef.current);
  }, [sub?.invoice?.id]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(`${WORKER_URL}/api/user/invoice/generate`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        await fetchSub();
      } else {
        setError(data.error || 'Erro ao gerar fatura');
      }
    } catch {
      setError('Erro ao gerar fatura');
    }
    setGenerating(false);
  };

  const handleCopy = () => {
    if (sub?.invoice?.pix_qr_code) {
      navigator.clipboard.writeText(sub.invoice.pix_qr_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <p style={{ color: '#888' }}>Carregando...</p>
      </div>
    );
  }

  if (paid) {
    return (
      <>
        <style>{`
          @keyframes popIn {
            0% { transform: scale(0); opacity: 0; }
            70% { transform: scale(1.15); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '70vh', gap: '24px' }}>
          <div style={{
            width: '96px', height: '96px', borderRadius: '50%',
            backgroundColor: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'popIn 0.5s ease forwards'
          }}>
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <path d="M10 24L20 34L38 14" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div style={{ textAlign: 'center', animation: 'fadeUp 0.5s ease 0.3s both' }}>
            <h2 style={{ color: '#16a34a', margin: 0, fontSize: '1.6rem' }}>Fatura paga!</h2>
          </div>
        </div>
      </>
    );
  }

  const formatDate = (d) => d ? d.split('-').reverse().join('/') : '';
  const invoice = sub?.invoice;
  const status = sub?.status;

  return (
    <div style={{ padding: '32px', maxWidth: '560px', margin: '0 auto' }}>
      <h2 style={{ color: '#2D3277', marginBottom: '8px' }}>Mensalidade</h2>
      <p style={{ color: '#666', marginBottom: '28px', fontSize: '0.9rem' }}>Integra MKP — R$ 49,90 / mês</p>

      {status === 'active' && !invoice && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '20px', marginBottom: '24px' }}>
          <p style={{ color: '#166534', fontWeight: 600, margin: 0 }}>✓ Assinatura ativa</p>
          {sub?.dueDate && <p style={{ color: '#555', fontSize: '0.85rem', margin: '6px 0 0' }}>Próximo vencimento: {formatDate(sub.dueDate)}</p>}
        </div>
      )}

      {(status === 'warning' || status === 'blocked') && !invoice && (
        <div style={{
          background: status === 'blocked' ? '#fef2f2' : '#fffbeb',
          border: `1px solid ${status === 'blocked' ? '#fecaca' : '#fde68a'}`,
          borderRadius: '10px', padding: '20px', marginBottom: '24px'
        }}>
          <p style={{ color: status === 'blocked' ? '#cc0000' : '#92400e', fontWeight: 600, margin: '0 0 6px' }}>
            {status === 'blocked' ? '🔒 Acesso bloqueado' : '⚠️ Pagamento em atraso'}
          </p>
          <p style={{ color: '#555', fontSize: '0.85rem', margin: 0 }}>
            Vencimento: {formatDate(sub?.dueDate)} · {sub?.daysOverdue} dia{sub?.daysOverdue !== 1 ? 's' : ''} em atraso
          </p>
        </div>
      )}

      {error && (
        <p style={{ color: '#cc0000', background: '#fef2f2', padding: '10px 14px', borderRadius: '6px', fontSize: '0.88rem', marginBottom: '16px' }}>{error}</p>
      )}

      {!invoice ? (
        <button
          onClick={handleGenerate}
          disabled={generating}
          style={{
            width: '100%', padding: '13px', backgroundColor: generating ? '#8890c4' : '#2D3277',
            color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem',
            fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer', transition: 'background 0.2s'
          }}
        >
          {generating ? 'Gerando fatura...' : 'Gerar fatura PIX — R$ 49,90'}
        </button>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ background: '#f0f7ff', borderRadius: '8px', padding: '16px', textAlign: 'center', marginBottom: '20px' }}>
            <p style={{ margin: 0, color: '#444', fontSize: '0.85rem' }}>Valor</p>
            <p style={{ margin: '4px 0 0', fontSize: '1.8rem', fontWeight: 700, color: '#2D3277' }}>R$ {Number(invoice.amount).toFixed(2).replace('.', ',')}</p>
            <p style={{ margin: '6px 0 0', color: '#888', fontSize: '0.8rem' }}>Vencimento: <strong>{formatDate(invoice.due_date)}</strong></p>
          </div>

          <p style={{ textAlign: 'center', fontWeight: 600, color: '#333', marginBottom: '14px', fontSize: '0.95rem' }}>
            Escaneie o QR Code para pagar via PIX
          </p>

          {invoice.pix_qr_code_base64 && (
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <img
                src={`data:image/png;base64,${invoice.pix_qr_code_base64}`}
                alt="QR Code PIX"
                width={220}
                height={220}
                style={{ borderRadius: '8px', border: '1px solid #eee' }}
              />
            </div>
          )}

          <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#888', marginBottom: '8px' }}>
            Ou copie o código PIX abaixo:
          </p>
          <div style={{
            background: '#f5f5f5', borderRadius: '6px', padding: '10px 14px',
            wordBreak: 'break-all', fontSize: '0.72rem', color: '#555',
            marginBottom: '10px', lineHeight: '1.5'
          }}>
            {invoice.pix_qr_code}
          </div>

          <button
            onClick={handleCopy}
            style={{
              width: '100%', padding: '10px', backgroundColor: copied ? '#16a34a' : '#f0f0f0',
              color: copied ? 'white' : '#333', border: 'none', borderRadius: '6px',
              fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s'
            }}
          >
            {copied ? '✓ Código copiado!' : 'Copiar código PIX'}
          </button>

          <p style={{ textAlign: 'center', color: '#999', fontSize: '0.75rem', marginTop: '16px', lineHeight: '1.5' }}>
            Aguardando confirmação do pagamento...
          </p>
        </div>
      )}
    </div>
  );
}
